// intlRegions.ts
// Single source of truth for international (non-TW) shipping regions in the OMS
// admin frontend. Adding a country is one entry here — the country filter, SF
// shipping modal defaults, return-label panel, and order-card labels all read
// from this registry instead of hardcoding 'HK'.
//
// Backend contract (docs/plans/intl_shipping_regions_2026-07.md §1/§4.1):
//   NEW orders:    delivery_method = 'intl',    shipping_country = 'HK' | 'MO' | 'SG'
//   LEGACY orders: delivery_method = 'intl_hk', shipping_country usually 'HK'
// An order is international when its shipping_country is a known intl code (not
// 'TW') OR its delivery_method is 'intl' / 'intl_hk'. Country = shipping_country,
// defaulting legacy intl_hk (and intl rows with a missing country) to 'HK'.
//
// ⚠️ CARRIER IS NOT ALWAYS 順豐. SG ships via **Parxl** (PLS / Singapore Airlines
// cross-border, last mile Ninjavan SG), NOT SF Express — mirroring the backend
// `carrier` field in services/intl-shipping.constants.js. Never hardcode 順豐 or
// assume an SF waybill for an intl order: read `region.carrierLabel`, and gate
// any SF action on `region.carrier === 'sf'` (or use isSfOrder / SF_REGIONS).
// The backend already rejects /sf/create and /sf/reship for non-SF regions, so a
// 順豐 button on an SG order is a guaranteed 400 — the UI must not offer it.

export type SfCountryCode = 'HK' | 'MO';
export type IntlCountryCode = SfCountryCode | 'SG';
export type IntlCarrier = 'sf' | 'parxl';

export interface IntlRegionOption {
  value: string; // SF IUOP regionSecond value (English; must match SF master list)
  label: string; // Display label (English + local script)
}

interface IntlRegionBase {
  code: IntlCountryCode;
  flag: string;
  nameZh: string;
  nameEn: string;
  phoneAreaCode: string; // '852' | '853' | '65' — digits only, no '+'
  carrier: IntlCarrier;
  // Display name of the carrier ('順豐' | 'Parxl'). Use this in every label,
  // tooltip and snackbar instead of writing 順豐 into a component.
  carrierLabel: string;
  // Customs note for the modal's info banner.
  customsNote: string;
}

// A region carried by SF Express (順豐 IUOP): has the extra address tiers the SF
// waybill payload requires. Only these regions may reach the SF modal / routes.
export interface SfIntlRegion extends IntlRegionBase {
  carrier: 'sf';
  regionFirst: string;   // SF regionFirst, e.g. 'Hong Kong' | 'Macau'
  // regionSecond tier options shown in the SF modal.
  regionOptions: IntlRegionOption[];
  // Optional regionThird tier keyed by regionSecond value (HK has districts; MO
  // ships with no third tier). Regions without districts render no district Select.
  districts?: Record<string, IntlRegionOption[]>;
  // Heuristic to pre-select regionSecond from a free-form address string.
  guessRegionSecond: (address: string) => string;
}

// A region carried by Parxl. There is no SF payload here at all — Parxl needs
// only a 6-digit postcode, no region/district tiers and no recipient ID. Booking
// goes through POST /orders/:id/parxl/create and the label comes back as PDF
// bytes the backend proxies (SF hands back a hosted URL instead).
export interface ParxlIntlRegion extends IntlRegionBase {
  carrier: 'parxl';
  // Parxl bills on a weight step function, so the admin must enter a real scale
  // reading before booking — unlike the SF lane, which declares a flat 5kg and
  // reconciles at pickup. The booking dialog makes weight the primary input.
  weightIsBillable: true;
}

export type IntlRegion = SfIntlRegion | ParxlIntlRegion;

// ─── Hong Kong ────────────────────────────────────────────────────────────────

const HK_DISTRICTS: Record<string, IntlRegionOption[]> = {
  'Hong Kong Island': [
    { value: 'Central and Western', label: 'Central and Western 中西區' },
    { value: 'Eastern', label: 'Eastern 東區' },
    { value: 'Southern', label: 'Southern 南區' },
    { value: 'Wan Chai', label: 'Wan Chai 灣仔區' },
  ],
  Kowloon: [
    { value: 'Kowloon City', label: 'Kowloon City 九龍城區' },
    { value: 'Kwun Tong', label: 'Kwun Tong 觀塘區' },
    { value: 'Sham Shui Po', label: 'Sham Shui Po 深水埗區' },
    { value: 'Wong Tai Sin', label: 'Wong Tai Sin 黃大仙區' },
    { value: 'Yau Tsim Mong', label: 'Yau Tsim Mong 油尖旺區' },
  ],
  'New Territories': [
    { value: 'Islands', label: 'Islands 離島區' },
    { value: 'Kwai Tsing', label: 'Kwai Tsing 葵青區' },
    { value: 'North', label: 'North 北區' },
    { value: 'Sai Kung', label: 'Sai Kung 西貢區' },
    { value: 'Sha Tin', label: 'Sha Tin 沙田區' },
    { value: 'Tai Po', label: 'Tai Po 大埔區' },
    { value: 'Tsuen Wan', label: 'Tsuen Wan 荃灣區' },
    { value: 'Tuen Mun', label: 'Tuen Mun 屯門區' },
    { value: 'Yuen Long', label: 'Yuen Long 元朗區' },
  ],
};

const guessHKRegionSecond = (address: string): string => {
  const a = (address || '').toLowerCase();
  if (/kowloon|九龍|九龙|tsim sha tsui|mong kok|yau ma tei|sham shui po/.test(a)) return 'Kowloon';
  if (/new territories|新界|sha tin|tsuen wan|tuen mun|yuen long|tai po|sai kung|islands|kwai tsing/.test(a))
    return 'New Territories';
  return 'Hong Kong Island';
};

const HK_REGION: SfIntlRegion = {
  code: 'HK',
  flag: '🇭🇰',
  nameZh: '香港',
  nameEn: 'Hong Kong',
  phoneAreaCode: '852',
  carrier: 'sf',
  carrierLabel: '順豐',
  regionFirst: 'Hong Kong',
  regionOptions: [
    { value: 'Hong Kong Island', label: 'Hong Kong Island 香港島' },
    { value: 'Kowloon', label: 'Kowloon 九龍' },
    { value: 'New Territories', label: 'New Territories 新界' },
  ],
  districts: HK_DISTRICTS,
  guessRegionSecond: guessHKRegionSecond,
  customsNote: '香港對服飾進口免關稅，不需提供 HKID。',
};

// ─── Macau ────────────────────────────────────────────────────────────────────

const guessMORegionSecond = (address: string): string => {
  const a = (address || '').toLowerCase();
  if (/taipa|氹仔|凼仔|cotai|路氹/.test(a)) return 'Taipa';
  if (/coloane|路環/.test(a)) return 'Coloane';
  return 'Macau Peninsula';
};

const MO_REGION: SfIntlRegion = {
  code: 'MO',
  flag: '🇲🇴',
  nameZh: '澳門',
  nameEn: 'Macau',
  phoneAreaCode: '853',
  carrier: 'sf',
  carrierLabel: '順豐',
  regionFirst: 'Macau',
  regionOptions: [
    { value: 'Macau Peninsula', label: 'Macau Peninsula 澳門半島' },
    { value: 'Taipa', label: 'Taipa 氹仔' },
    { value: 'Coloane', label: 'Coloane 路環' },
  ],
  // No SF regionThird tier for MO.
  guessRegionSecond: guessMORegionSecond,
  customsNote: '澳門為自由港，服飾進口免關稅。',
};

// ─── Singapore (Parxl — NOT 順豐) ─────────────────────────────────────────────

const SG_REGION: ParxlIntlRegion = {
  code: 'SG',
  flag: '🇸🇬',
  nameZh: '新加坡',
  nameEn: 'Singapore',
  phoneAreaCode: '65',
  carrier: 'parxl',
  carrierLabel: 'Parxl',
  weightIsBillable: true,
  customsNote: '新加坡：Parxl DDP（關稅由我們負擔），末端配送 Ninjavan SG；地址須含 6 位數郵遞區號。',
};

// ─── Registry ─────────────────────────────────────────────────────────────────

export const INTL_REGIONS: Record<IntlCountryCode, IntlRegion> = {
  HK: HK_REGION,
  MO: MO_REGION,
  SG: SG_REGION,
};

// SF-only view of the registry. Consumers that build an SF waybill (the SF
// shipping modal, the HK return-label panel) take this instead of INTL_REGIONS so
// the SF-specific fields are statically guaranteed and SG cannot be passed in.
export const SF_REGIONS: Record<SfCountryCode, SfIntlRegion> = {
  HK: HK_REGION,
  MO: MO_REGION,
};

export const INTL_REGION_LIST: IntlRegion[] = Object.values(INTL_REGIONS);

// A known international country code (case-insensitive), else false.
export const isIntlCountryCode = (c?: string | null): c is IntlCountryCode =>
  !!c && c.toUpperCase() in INTL_REGIONS;

// A country carried by SF Express. Gate every 順豐 action on this.
export const isSfCountryCode = (c?: string | null): c is SfCountryCode =>
  !!c && c.toUpperCase() in SF_REGIONS;

// Pull the raw country / delivery-method off an order-shaped object, tolerating
// both the raw snake_case DB row and the normalized UnifiedOrder camelCase, and a
// nested `.original` DB row (admin objects wrap the storefront row there).
const readCountry = (order: any): string => {
  const orig = order?.original || {};
  return String(
    order?.shipping_country ?? order?.shippingCountry ?? orig.shipping_country ?? orig.shippingCountry ?? ''
  ).toUpperCase();
};
const readDeliveryMethod = (order: any): string => {
  const orig = order?.original || {};
  return String(order?.delivery_method ?? order?.deliveryMethod ?? orig.delivery_method ?? orig.deliveryMethod ?? '');
};

// True when the order ships internationally (known intl country OR intl* method).
export function isIntlOrder(order: any): boolean {
  if (!order) return false;
  if (isIntlCountryCode(readCountry(order))) return true;
  const dm = readDeliveryMethod(order);
  return dm === 'intl' || dm === 'intl_hk';
}

// The region code of an intl order, or null for a domestic (TW) order. Legacy
// intl_hk rows — and any intl row with a missing/unknown country — resolve to 'HK'.
export function intlCountryOf(order: any): IntlCountryCode | null {
  if (!isIntlOrder(order)) return null;
  const c = readCountry(order);
  return isIntlCountryCode(c) ? c : 'HK';
}

// The full region config for an intl order, or null for a domestic order.
export function intlRegionOf(order: any): IntlRegion | null {
  const code = intlCountryOf(order);
  return code ? INTL_REGIONS[code] : null;
}

// The carrier for an intl order ('sf' | 'parxl'), or null for a domestic order.
export function intlCarrierOf(order: any): IntlCarrier | null {
  return intlRegionOf(order)?.carrier ?? null;
}

// True for intl orders that SF Express actually carries — i.e. the only orders
// that may open the SF modal or hit /orders/:id/sf/*. An SG order is intl but
// NOT an SF order.
export function isSfIntlOrder(order: any): boolean {
  return intlCarrierOf(order) === 'sf';
}

// The SF country code of an order, or null when it is domestic or not SF-carried.
export function sfCountryOf(order: any): SfCountryCode | null {
  const c = intlCountryOf(order);
  return c && isSfCountryCode(c) ? c : null;
}
